import React, { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAccounts } from '../context/AccountContext';
import { useTranslation } from "react-i18next";
import { Header } from '@uhg-abyss/web/ui/Header';
import { NavMenu } from '@uhg-abyss/web/ui/NavMenu';
import { styled } from '@uhg-abyss/web/tools/styled';

const StyledHeaderContainer = styled(Header.Container, {
    '&.abyss-header-root': {
        width: '100%',
        maxWidth: 'none',
        backgroundColor: '$surfaceWhite',
    },
    '& .abyss-header-container': {
        width: '100%',
        maxWidth: 'none',
        backgroundColor: '$surfaceWhite',
    },
});

const StyledHeaderMainContent = styled(Header.MainContent, {
    '&.abyss-header-main-content': {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
});

const StyledHeaderActions = styled(Header.Actions, {
    '&.abyss-header-actions': {
        marginLeft: 'auto',
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%',
        maxWidth: 'none',
        flex: '1 1 auto',
    },
    '&.abyss-header-actions > *': {
        marginLeft: 'auto',
        display: 'flex',
        justifyContent: 'flex-end',
        width: 'fit-content',
    },
});

const StyledNavMenuRoot = styled(NavMenu.Root, {
    '&.abyss-nav-menu-root': {
        marginLeft: 'auto',
        display: 'flex',
        justifyContent: 'flex-end',
        width: 'fit-content',
        maxWidth: 'none',
        flex: '0 0 auto',
    },
    '&.abyss-nav-menu-root .abyss-nav-menu-container': {
        display: 'flex',
        justifyContent: 'flex-end',
        width: 'fit-content',
        marginLeft: 'auto',
    },
    '&.abyss-nav-menu-root .abyss-nav-menu-link-root': {
        color: '$interactiveNav',
    },
    '&.abyss-nav-menu-root .abyss-nav-menu-link': {
        color: '$interactiveNav',
        textAlign: 'left',
    },
    '&.abyss-nav-menu-root .abyss-nav-menu-menu-trigger': {
        color: '$interactiveNav',
    },
    '&.abyss-nav-menu-root .abyss-nav-menu-item-title': {
        color: '$interactiveNav',
    },
});

const StyledNavMenuList = styled(NavMenu.List, {
    '&.abyss-nav-menu-list': {
        display: 'flex',
        justifyContent: 'flex-end',
        width: 'fit-content',
        marginLeft: 'auto',
        marginRight: 0,
        paddingRight: 0,
    },
});

export const HeaderNavMenu = () => {
    const { isAuthenticated } = useAuth();
    const { displayName, unlockAccount } = useAccounts();
    const { t } = useTranslation();
    const location = useLocation();
    const nav = useNavigate();
    const [, setNavigationCount] = useState(0);
    const hasMounted = useRef(false);
    const navToNewPassword = () => { nav('/new-password') };
    const navToTestPassword = () => { nav('/test-password') };
    const navToLogout = () => { nav('/logout') };

    const isOnTestPassword = window.location.href.indexOf("test-password") > -1;
    const isOnNewPassword = window.location.href.indexOf("new-password") > -1;
    const isOnHome = window.location.pathname === "/" || window.location.pathname === "/home";

    React.useEffect(() => {
        // runs on location, i.e. route, change
        setNavigationCount((previousCount) => previousCount + 1);

        if (!hasMounted.current) {
            hasMounted.current = true;
            return;
        }

        const isHomeRoute = location.pathname === '/' || location.pathname === '/home';
        if (isAuthenticated && isHomeRoute) {
            unlockAccount();
        }
    }, [location, isAuthenticated, unlockAccount]);

    return (
        <StyledHeaderContainer>
            <StyledHeaderMainContent>
                <Header.Brandmark logo={<img src="optumhelpme-logo.svg" alt="Optum HelpMe" />} logoRedirect="/" />
                <StyledHeaderActions>
                    <StyledNavMenuRoot mobileDrawer={false}>
                        <StyledNavMenuList>
                            {isAuthenticated ? (
                                <>
                                    {isOnTestPassword && (
                                        <NavMenu.Link onClick={navToNewPassword}>{t('setNewPassword')}</NavMenu.Link>
                                    )}
                                    {isOnNewPassword && (
                                        <NavMenu.Link onClick={navToTestPassword}>{t('testPassword')}</NavMenu.Link>
                                    )}
                                    {isOnHome ? (
                                        <NavMenu.Link onClick={navToLogout}>{t('logout')}</NavMenu.Link>
                                    ) : (
                                        <NavMenu.Item>
                                            <NavMenu.Trigger>{displayName}</NavMenu.Trigger>
                                            <NavMenu.Content>
                                                <NavMenu.Columns>
                                                    <NavMenu.Column>
                                                        <NavMenu.MenuItem
                                                            title={t('logout')}
                                                            onClick={navToLogout}
                                                        />
                                                    </NavMenu.Column>
                                                </NavMenu.Columns>
                                            </NavMenu.Content>
                                        </NavMenu.Item>
                                    )}
                                </>
                            ) : (
                                <NavMenu.Link href="/login">{t('login')}</NavMenu.Link>
                            )}
                        </StyledNavMenuList>
                    </StyledNavMenuRoot>
                </StyledHeaderActions>
            </StyledHeaderMainContent>
        </StyledHeaderContainer>
    );
}